import { FormField, Input, useToast, type DataTableProps, type MenuItem } from '@ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { FormDialog } from '../../../app/FormDialog';
import { useRowMenu, useLoadedForAction } from '../../../app/rowActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import {
  invoicePdfUrl,
  useInvoice,
  useInvoiceAction,
  useSaleAction,
  type InvoiceListItem,
} from '../api';
import { PaymentDialog } from './PaymentDialog';

type Open = { kind: 'send' | 'pay' | 'cancel'; invoice: InvoiceListItem };

/**
 * The `…` menu of an invoice row (#197): PDF, e-mail, payment and the
 * cancellation invoice, without leaving the list.
 */
export function useInvoiceRowActions(): {
  rowActions: NonNullable<DataTableProps<InvoiceListItem>['rowActions']>;
  dialogs: ReactNode;
} {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const action = useInvoiceAction();
  const saleAction = useSaleAction();
  const [open, setOpen] = useState<Open | null>(null);
  const write = can('invoices.write');
  const close = () => {
    action.reset();
    setOpen(null);
  };

  const items = (i: InvoiceListItem): (MenuItem | false)[] => {
    const final = i.status === 'final';
    const original = i.kind === 'invoice';
    const act = (kind: Open['kind']) => () => setOpen({ kind, invoice: i });
    return [
      i.status !== 'draft' && {
        key: 'pdf',
        label: t('sales.quick.showPdf'),
        // The version only busts the cache; the list does not know it.
        onSelect: () => window.open(invoicePdfUrl(i.id, Date.now()), '_blank', 'noopener'),
      },
      write && final && { key: 'send', label: t('sales.invoice.send'), onSelect: act('send') },
      write &&
        final &&
        original &&
        !i.paid && { key: 'pay', label: t('sales.detail.pay'), onSelect: act('pay') },
      write && final && original && rowMenu.remove(act('cancel'), t('sales.invoice.cancel')),
    ];
  };

  const invoice = open?.invoice;
  const dialogs = (
    <>
      {open?.kind === 'send' && invoice && <SendInvoice id={invoice.id} onClose={close} />}
      {open?.kind === 'pay' && invoice && (
        <PaymentDialog
          open
          onClose={close}
          pending={action.isPending}
          error={action.error}
          onSubmit={(method, on) =>
            action.mutate(
              { id: invoice.id, action: 'pay', method, on },
              {
                onSuccess: () => {
                  // The payment belongs to the sale (INVC-010), so undo resets it there.
                  toast.show(
                    t('sales.detail.paid'),
                    'success',
                    can('sales.write')
                      ? {
                          label: t('common.undo'),
                          onClick: () =>
                            saleAction.mutate(
                              { id: invoice.sale_id, action: 'unpay' },
                              { onError: (e) => toast.show(errorMessage(e), 'danger') },
                            ),
                        }
                      : undefined,
                  );
                  close();
                },
              },
            )
          }
        />
      )}
      <ConfirmDialog
        open={open?.kind === 'cancel'}
        onClose={close}
        title={t('sales.invoice.cancelTitle', { number: invoice?.number })}
        description={t('sales.invoice.cancelText')}
        confirmLabel={t('sales.invoice.cancel')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          invoice &&
          action.mutate(
            { id: invoice.id, action: 'cancel' },
            {
              onSuccess: () => {
                toast.show(t('sales.invoice.cancelled'));
                close();
              },
            },
          )
        }
      />
    </>
  );

  return {
    rowActions: rowMenu.table<InvoiceListItem>(
      (i) => i.number ?? t('sales.invoice.badge.draft'),
      items,
    ),
    dialogs,
  };
}

/** Loads the invoice for its recipient's address, then asks where to send it. */
function SendInvoice({ id, onClose }: { id: string; onClose: () => void }) {
  const invoice = useLoadedForAction(useInvoice(id), onClose);
  return invoice ? (
    <SendDialog
      id={id}
      number={invoice.number ?? ''}
      email={invoice.customer_email ?? ''}
      onClose={onClose}
    />
  ) : null;
}

function SendDialog({
  id,
  number,
  email,
  onClose,
}: {
  id: string;
  number: string;
  email: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const action = useInvoiceAction();
  const [to, setTo] = useState(email);
  const errors = formErrors(action.error, { to: ['email.', 'invoice.email_missing'] });
  return (
    <FormDialog
      open
      onClose={onClose}
      title={t('sales.invoice.sendTitle', { number })}
      submitLabel={t('sales.invoice.send')}
      pending={action.isPending}
      error={errors.general}
      onSubmit={() =>
        action.mutate(
          { id, action: 'send', to: to.trim() || undefined },
          {
            onSuccess: () => {
              toast.show(t('sales.invoice.sent', { to: to.trim() || email }));
              onClose();
            },
          },
        )
      }
    >
      <FormField label={t('sales.invoice.to')} required error={errors.field.to}>
        {(p) => <Input {...p} type="email" value={to} onChange={(e) => setTo(e.target.value)} />}
      </FormField>
    </FormDialog>
  );
}
